import React, { useEffect, useState } from 'react';
import {
  StyledMenu,
  StyledMenuItem,
  StyledWarningIcon
} from './styledComponents';
import { useSelector } from 'react-redux';
import { sideMenuContents } from './SideMenuConfig';
import BillingsIcon from '../../../../Assets/Icons/BillingsIcon';
import { Link, useLocation } from 'react-router-dom';
import { Tooltip, Badge } from 'antd';
import { getAccessData } from '../../../../Apis/Patient Roster';
import { getBillingAdminJobs } from '../../../../Apis/Biller';

const BILLING_ADMIN_LINK = '/dashboard/billing-admin';
const ORPHAN_COUNT_EVENT = 'billing-admin:orphan-count';
const TWO_MONTHS_MS = 60 * 24 * 60 * 60 * 1000;

function SideMenu({ collapsed }) {
  const [theme] = useSelector(state => [state.themeReducer]);
  const location = useLocation();
  const [sideMenuItems, setSideMenuItems] = useState([]);
  const [user] = useSelector(state => [state.userReducer]);
  const [access, setAccess] = useState(true);
  const [orphanCount, setOrphanCount] = useState(0);
  const [activeKey, setActiveKey] = useState(() => {
    const activeItem = sideMenuItems.find(item => {
      let splitPath = location.pathname.split('/');
      let splitLink = item.link.split('/');
      // Check for exact match first
      if (splitPath[2] === splitLink[2]) return item;
      // Check if it's a dictionary-related route (dictionary-facility, dictionary-templates)
      if (splitLink[2] === 'dictionary' && splitPath[2]?.startsWith('dictionary')) return item;
      // Check if it's a facility-management route
      if (splitLink[2] === 'facility-management' && splitPath[2]?.startsWith('facility-management')) return item;
      return null;
    });
    return activeItem ? activeItem.key : '0';
  });

  useEffect(() => {
    if (!user) return;
    const items = sideMenuContents(user.userType) || [];
    const hasBillerAdminRole =
      user.userType === 'clinicAdmin' ||
      ((user.userType === 'biller' || user.userType === 'admin') &&
      (user.clinics || []).some(c => c.billerAdmin));
    if (hasBillerAdminRole) {
      const extra = [];
      if (!items.find(i => i.link === '/dashboard/billing-admin')) {
        extra.push({ key: 'billing-admin', name: 'Billing Admin', link: '/dashboard/billing-admin', icon: BillingsIcon });
      }
      setSideMenuItems(extra.length ? [...items, ...extra] : items);
    } else {
      setSideMenuItems(items);
    }
    // eslint-disable-next-line
  }, [user]);
  useEffect(() => {
    if (user.userType !== 'reviewer' && user.userType !== 'scribe') {
      getAccessData()
        .then(response => {
          if (response.data.success) {
            // console.log('test1', authorId, access);
            setAccess(true);
            // setLoader(false);
            // setSearchStringFacility(new String(''));
          } else {
            // console.log(access, 'access1');
            setAccess(false);

            // setLoader(false);
          }
        })
        .catch(err => console.log(err));
    }
  }, []);

  // Orphaned-job badge on the Billing Admin menu item (TRPA-8545 / TRPA-8371 AC3).
  // Initial count is fetched here; the Billing Admin page broadcasts updated counts
  // via ORPHAN_COUNT_EVENT so the badge stays in sync after fetches/reassignments.
  useEffect(() => {
    if (!user) return;
    const billerAdminClinics = (user.clinics || []).filter(c => c.billerAdmin);
    const hasBillingAdminAccess =
      user.userType === 'billingAdmin' ||
      user.userType === 'clinicAdmin' ||
      ((user.userType === 'biller' || user.userType === 'admin') &&
        billerAdminClinics.length > 0);
    if (!hasBillingAdminAccess) return;

    const to = Date.now();
    const payload = { createdFrom: to - TWO_MONTHS_MS, createdTo: to };
    // Multi-clinic billers need a selected clinic; the page handles that case and
    // will broadcast the count. For a single billerAdmin clinic we can resolve it here.
    if (user.userType === 'biller') {
      if (billerAdminClinics.length === 1) {
        payload.clinicId = billerAdminClinics[0].clinicId;
      } else {
        return;
      }
    }

    getBillingAdminJobs(payload)
      .then(res => {
        if (res.data.success) {
          const count = (res.data.result || []).filter(j => j.billingUnroutable).length;
          setOrphanCount(count);
        }
      })
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    const handler = e => setOrphanCount(e.detail || 0);
    window.addEventListener(ORPHAN_COUNT_EVENT, handler);
    return () => window.removeEventListener(ORPHAN_COUNT_EVENT, handler);
  }, []);

  useEffect(() => {
    const splitPath = location.pathname.split('/');
    const isEditorRoute = splitPath[2] === 'new-editor' || splitPath[2] === 'editor';

    const activeItem =
      sideMenuItems &&
      sideMenuItems.find(item => {
        let splitLink = item.link.split('/');
        // Map editor routes to the Jobs sidebar item
        if (isEditorRoute && splitLink[2] === 'jobs-list') return item;
        // Check for exact match first
        if (splitPath[2] === splitLink[2]) return item;
        // Check if it's a dictionary-related route (dictionary-facility, dictionary-templates)
        if (splitLink[2] === 'dictionary' && splitPath[2]?.startsWith('dictionary')) return item;
        // Check if it's a facility-management route
        if (splitLink[2] === 'facility-management' && splitPath[2]?.startsWith('facility-management')) return item;
        return null;
      });
    if (activeItem) {
      setActiveKey(activeItem.key);
    } else {
      setActiveKey('0');
    }
  }, [sideMenuItems, location.pathname]);

  const onSelect = param => {
    setActiveKey(param.key);
  };

  return (
    <StyledMenu
      mode='inline'
      defaultSelectedKeys={activeKey}
      theme='light'
      color={theme['@primary-color']}
      textColor={theme['@text-color']}
      collapsed={collapsed}
      onSelect={onSelect}
      key={activeKey}
    >
      {sideMenuItems &&
        sideMenuItems
          .filter(
            config =>
              !(
                !access &&
                config.name === 'Roster' &&
                user.userType !== 'biller'
              )
          )
          .map(config => {
            return (
              <StyledMenuItem
                key={config.key}
                icon={<config.icon />}
                color={theme['@primary-color']}
                disabled={config.disabled || (config.name === 'HL7' && true)}
              >
                <Link to={config.link}>{config.name}</Link>
                {config.link === BILLING_ADMIN_LINK && orphanCount > 0 && (
                  <Tooltip title={`${orphanCount} orphaned job${orphanCount > 1 ? 's' : ''}`}>
                    <Badge
                      count={orphanCount}
                      overflowCount={99}
                      style={{
                        marginLeft: 8,
                        backgroundColor: '#fff',
                        color: theme['@text-color'],
                        boxShadow: `0 0 0 1px ${theme['@text-color']}`,
                        minWidth: 20,
                        height: 20,
                        lineHeight: '20px',
                        padding: 0,
                        borderRadius: '50%',
                        transform: 'translateY(-8px)'
                      }}
                    />
                  </Tooltip>
                )}
                {config.name === 'Settings' &&
                  user.userType === 'admin' &&
                  !user.clinicConfigured && (
                    <Link to={config.link}>
                      <Tooltip title='Ehr not Configured.'>
                        <StyledWarningIcon />
                      </Tooltip>
                    </Link>
                  )}
              </StyledMenuItem>
            );
          })}
    </StyledMenu>
  );
}

export default SideMenu;
